import { Suspense, lazy } from "react";

import { createBrowserRouter } from "react-router-dom";

import { AppShell } from "@/components/layout/app-shell";
import { ProtectedRoute } from "@/features/auth/components/protected-route";
import { RouteFallback } from "@/routes/route-fallback";

// Every leaf page is code-split so the initial bundle only pays for the
// app shell + whichever page the user actually lands on.
const LoginPage = lazy(() =>
  import("@/features/auth/components/login-page").then((m) => ({ default: m.LoginPage })),
);
const ProfilePage = lazy(() =>
  import("@/features/auth/components/profile-page").then((m) => ({
    default: m.ProfilePage,
  })),
);
const DashboardPage = lazy(() =>
  import("@/pages/dashboard-page").then((m) => ({ default: m.DashboardPage })),
);
const NotFoundPage = lazy(() =>
  import("@/pages/not-found-page").then((m) => ({ default: m.NotFoundPage })),
);

// Nhân viên (staff accounts)
const StaffListPage = lazy(() =>
  import("@/features/users/components/users-list-page").then((m) => ({
    default: m.UsersListPage,
  })),
);
const StaffDetailPage = lazy(() =>
  import("@/features/users/components/user-detail-page").then((m) => ({
    default: m.UserDetailPage,
  })),
);

// Kho hàng (warehouse / devices)
const WarehouseListPage = lazy(() =>
  import("@/features/warehouse/components/warehouse-list-page").then((m) => ({
    default: m.WarehouseListPage,
  })),
);
const WarehouseFormPage = lazy(() =>
  import("@/features/warehouse/components/warehouse-form-page").then((m) => ({
    default: m.WarehouseFormPage,
  })),
);
const WarehouseDetailPage = lazy(() =>
  import("@/features/warehouse/components/warehouse-detail-page").then((m) => ({
    default: m.WarehouseDetailPage,
  })),
);
// Link public bảng hàng — không cần đăng nhập, gửi cho đối tác. Route ở ngoài ProtectedRoute
// (xem bên dưới), không dùng AppShell.
const PublicCatalogPage = lazy(() =>
  import("@/features/warehouse/components/public-catalog-page").then((m) => ({
    default: m.PublicCatalogPage,
  })),
);

// Phụ kiện (accessories)
const AccessoriesListPage = lazy(() =>
  import("@/features/accessories/components/accessories-list-page").then((m) => ({
    default: m.AccessoriesListPage,
  })),
);
const AccessoryFormPage = lazy(() =>
  import("@/features/accessories/components/accessory-form-page").then((m) => ({
    default: m.AccessoryFormPage,
  })),
);

// Gói bảo hành (warranty packages)
const WarrantyPackagesListPage = lazy(() =>
  import("@/features/warranty-packages/components/warranty-packages-list-page").then(
    (m) => ({
      default: m.WarrantyPackagesListPage,
    }),
  ),
);
const WarrantyPackageFormPage = lazy(() =>
  import("@/features/warranty-packages/components/warranty-package-form-page").then(
    (m) => ({
      default: m.WarrantyPackageFormPage,
    }),
  ),
);

// Khách hàng (clients)
const ClientsListPage = lazy(() =>
  import("@/features/clients/components/clients-list-page").then((m) => ({
    default: m.ClientsListPage,
  })),
);
const ClientFormPage = lazy(() =>
  import("@/features/clients/components/client-form-page").then((m) => ({
    default: m.ClientFormPage,
  })),
);
const ClientDetailPage = lazy(() =>
  import("@/features/clients/components/client-detail-page").then((m) => ({
    default: m.ClientDetailPage,
  })),
);

// Giao dịch (transactions)
const TransactionsListPage = lazy(() =>
  import("@/features/transactions/components/transactions-list-page").then((m) => ({
    default: m.TransactionsListPage,
  })),
);
const TransactionDetailPage = lazy(() =>
  import("@/features/transactions/components/transaction-detail-page").then((m) => ({
    default: m.TransactionDetailPage,
  })),
);
const SaleWizardPage = lazy(() =>
  import("@/features/transactions/components/sale-wizard/sale-wizard-page").then((m) => ({
    default: m.SaleWizardPage,
  })),
);
const RepairWizardPage = lazy(() =>
  import("@/features/transactions/components/repair-wizard/repair-wizard-page").then(
    (m) => ({ default: m.RepairWizardPage }),
  ),
);

// Tài chính kế toán (finance)
const StatisticsOverviewPage = lazy(() =>
  import("@/features/finance/components/statistics-overview-page").then((m) => ({
    default: m.StatisticsOverviewPage,
  })),
);
const StaffPerformancePage = lazy(() =>
  import("@/features/finance/components/staff-performance-page").then((m) => ({
    default: m.StaffPerformancePage,
  })),
);
const InventoryDebtPage = lazy(() =>
  import("@/features/finance/components/inventory-debt-page").then((m) => ({
    default: m.InventoryDebtPage,
  })),
);

// Cấu hình hệ thống (system settings)
const MetadataPage = lazy(() =>
  import("@/features/settings/components/metadata-page").then((m) => ({
    default: m.MetadataPage,
  })),
);
const PermissionGroupsListPage = lazy(() =>
  import("@/features/permission-groups/components/permission-groups-list-page").then(
    (m) => ({
      default: m.PermissionGroupsListPage,
    }),
  ),
);
const PermissionGroupDetailPage = lazy(() =>
  import("@/features/permission-groups/components/permission-group-detail-page").then(
    (m) => ({ default: m.PermissionGroupDetailPage }),
  ),
);
const TableColumnsPage = lazy(() =>
  import("@/features/permission-groups/components/table-columns-page").then((m) => ({
    default: m.TableColumnsPage,
  })),
);

function withFallback(element: React.ReactNode) {
  return <Suspense fallback={<RouteFallback />}>{element}</Suspense>;
}

export const router = createBrowserRouter([
  {
    path: "/login",
    element: withFallback(<LoginPage />),
  },
  {
    path: "/public/kho-hang",
    element: withFallback(<PublicCatalogPage />),
  },
  {
    element: <ProtectedRoute />,
    children: [
      {
        element: <AppShell />,
        children: [
          { index: true, element: withFallback(<DashboardPage />) },
          {
            path: "dashboard",
            handle: { breadcrumb: "Tổng quan" },
            element: withFallback(<DashboardPage />),
          },
          {
            path: "user-info",
            handle: { breadcrumb: "Thông tin tài khoản" },
            element: withFallback(<ProfilePage />),
          },
          {
            path: "warehouse-management",
            handle: { breadcrumb: "Kho hàng" },
            children: [
              { index: true, element: withFallback(<WarehouseListPage />) },
              {
                path: "create",
                handle: { breadcrumb: "Nhập máy" },
                element: withFallback(<WarehouseFormPage />),
              },
              {
                path: ":productId",
                handle: { breadcrumb: "Chi tiết mã máy" },
                element: withFallback(<WarehouseDetailPage />),
              },
              {
                path: ":productId/edit",
                handle: { breadcrumb: "Chỉnh sửa mã máy" },
                element: withFallback(<WarehouseFormPage />),
              },
            ],
          },
          {
            path: "accessories-management",
            handle: { breadcrumb: "Phụ kiện" },
            children: [
              { index: true, element: withFallback(<AccessoriesListPage />) },
              {
                path: "create",
                handle: { breadcrumb: "Thêm phụ kiện" },
                element: withFallback(<AccessoryFormPage />),
              },
              {
                path: ":accessoryId/edit",
                handle: { breadcrumb: "Chỉnh sửa phụ kiện" },
                element: withFallback(<AccessoryFormPage />),
              },
            ],
          },
          {
            path: "warranty-packages-management",
            handle: { breadcrumb: "Gói bảo hành" },
            children: [
              { index: true, element: withFallback(<WarrantyPackagesListPage />) },
              {
                path: "create",
                handle: { breadcrumb: "Thêm gói bảo hành" },
                element: withFallback(<WarrantyPackageFormPage />),
              },
              {
                path: ":warrantyPackageId/edit",
                handle: { breadcrumb: "Chỉnh sửa gói bảo hành" },
                element: withFallback(<WarrantyPackageFormPage />),
              },
            ],
          },
          {
            path: "client-management",
            handle: { breadcrumb: "Khách hàng" },
            children: [
              { index: true, element: withFallback(<ClientsListPage />) },
              {
                path: "create",
                handle: { breadcrumb: "Thêm khách hàng" },
                element: withFallback(<ClientFormPage />),
              },
              {
                path: ":clientId",
                handle: { breadcrumb: "Chi tiết khách hàng" },
                element: withFallback(<ClientDetailPage />),
              },
              {
                path: ":clientId/edit",
                handle: { breadcrumb: "Chỉnh sửa khách hàng" },
                element: withFallback(<ClientFormPage />),
              },
            ],
          },
          {
            path: "transaction-management",
            handle: { breadcrumb: "Giao dịch" },
            children: [
              { index: true, element: withFallback(<TransactionsListPage />) },
              {
                path: "sale/new",
                handle: { breadcrumb: "Tạo đơn bán hàng" },
                element: withFallback(<SaleWizardPage />),
              },
              {
                path: "repair/new",
                handle: { breadcrumb: "Tạo đơn sửa chữa" },
                element: withFallback(<RepairWizardPage />),
              },
              {
                path: ":transactionId",
                handle: { breadcrumb: "Chi tiết giao dịch" },
                element: withFallback(<TransactionDetailPage />),
              },
            ],
          },
          {
            path: "admin-management",
            handle: { breadcrumb: "Nhân sự" },
            children: [
              { index: true, element: withFallback(<StaffListPage />) },
              {
                path: ":userId",
                handle: { breadcrumb: "Chi tiết nhân viên" },
                element: withFallback(<StaffDetailPage />),
              },
            ],
          },
          {
            path: "finance/overview",
            handle: { breadcrumb: "Tổng quan cửa hàng" },
            element: withFallback(<StatisticsOverviewPage />),
          },
          {
            path: "finance/staff-performance",
            handle: { breadcrumb: "KPI & lương nhân viên" },
            element: withFallback(<StaffPerformancePage />),
          },
          {
            path: "finance/inventory-debt",
            handle: { breadcrumb: "Tồn kho & công nợ" },
            element: withFallback(<InventoryDebtPage />),
          },
          {
            path: "settings/metadata",
            handle: { breadcrumb: "Quản lý metadata" },
            element: withFallback(<MetadataPage />),
          },
          {
            path: "settings/permission-groups",
            handle: { breadcrumb: "Phân quyền" },
            children: [
              { index: true, element: withFallback(<PermissionGroupsListPage />) },
              {
                path: ":groupId",
                handle: { breadcrumb: "Chi tiết nhóm quyền" },
                element: withFallback(<PermissionGroupDetailPage />),
              },
            ],
          },
          {
            path: "settings/table-columns",
            handle: { breadcrumb: "Quản lý xem bảng" },
            element: withFallback(<TableColumnsPage />),
          },
        ],
      },
    ],
  },
  { path: "*", element: withFallback(<NotFoundPage />) },
]);
